import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, Loader2, ChevronRight, TrendingDown, TrendingUp } from "lucide-react";
import { API } from "@/App";
import axios from "axios";

const CATEGORIES = [
  "Large Cap", "Mid Cap", "Small Cap", "Multi Cap", "ELSS",
  "Hybrid", "Sectoral", "International"
];

export const VolatilityRankingPage = () => {
  const [category, setCategory] = useState("Large Cap");
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchFunds = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/funds/search?q=${category}&limit=20`);
        const filtered = (res.data.funds || [])
          .filter(f => f.planType === "Direct" && f.option === "Growth")
          .map(fund => ({
            ...fund,
            // Mock volatility data
            stdDev: (Math.random() * 20 + 5).toFixed(2),
            sharpeRatio: (Math.random() * 2 + 0.5).toFixed(2),
            beta: (Math.random() * 0.5 + 0.75).toFixed(2)
          }));
        setFunds(filtered.sort((a, b) => a.stdDev - b.stdDev));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchFunds();
  }, [category]);

  const getRiskColor = (stdDev) => {
    if (stdDev < 10) return "text-green-500";
    if (stdDev < 15) return "text-yellow-500";
    if (stdDev < 20) return "text-orange-500";
    return "text-red-500";
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-4">
              Mutual Fund <span className="gold-text">Volatility Ranking</span>
            </h1>
            <p className="text-muted-foreground">
              Compare funds by risk metrics - Standard Deviation, Sharpe Ratio & Beta
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <Card className="bg-card border-border mb-8">
              <CardContent className="pt-6">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Category</label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-primary" />
                    {category} - Volatility Metrics (Sorted by Risk)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">#</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Fund Name</th>
                          <th className="text-center py-3 px-2 text-sm font-medium text-muted-foreground">Std Dev</th>
                          <th className="text-center py-3 px-2 text-sm font-medium text-muted-foreground">Sharpe</th>
                          <th className="text-center py-3 px-2 text-sm font-medium text-muted-foreground">Beta</th>
                          <th className="text-right py-3 px-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {funds.map((fund, idx) => (
                          <tr key={fund.schemeCode} className="border-b border-border/50 hover:bg-secondary/50">
                            <td className="py-3 px-2 text-sm">{idx + 1}</td>
                            <td className="py-3 px-2">
                              <p className="font-medium text-sm line-clamp-1">{fund.schemeName}</p>
                            </td>
                            <td className={`py-3 px-2 text-center font-medium ${getRiskColor(fund.stdDev)}`}>
                              {fund.stdDev}%
                            </td>
                            <td className="py-3 px-2 text-center">
                              <span className={fund.sharpeRatio > 1 ? "text-green-500" : "text-muted-foreground"}>
                                {fund.sharpeRatio}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-center text-muted-foreground">{fund.beta}</td>
                            <td className="py-3 px-2 text-right">
                              <Link to={`/research/${fund.schemeCode}`}>
                                <ChevronRight className="w-4 h-4 text-muted-foreground hover:text-primary" />
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="mt-6 p-4 rounded-lg bg-secondary/50 text-sm text-muted-foreground">
                    <p><strong>Std Dev:</strong> Higher = More volatile | <strong>Sharpe:</strong> Higher = Better risk-adjusted returns | <strong>Beta:</strong> &gt;1 = More volatile than market</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
